import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import CanvasStage from './canvas/CanvasStage';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import Toolbar from './Toolbar';
import BottomBar from './BottomBar';
import useCanvasTools from './hooks/useCanvasTools';
import useUndoRedo from './hooks/useUndoRedo';
import useWallInference from './hooks/useWallInference';
import useGridSnap from './hooks/useGridSnap';
import { VIRTUAL_SIZE, isWetRoom } from './constants';

/**
 * PlantaCleanEditor — Canvas editor for clean floor plans.
 *
 * Props:
 * - comodos: Array from motor (with bbox 0-1, nome, area, familia, esquadrias, acabamento)
 * - imagemPlanta: Base64 or URL of original plant image (for overlay)
 * - projectName: Project name for display
 * - onConfirm: Callback when user clicks "Calcular Orçamento" — receives updated rooms
 * - onRoomsChange: Callback when rooms data changes (for parent sync)
 */
export default function PlantaCleanEditor({
  comodos: initialComodos = [],
  imagemPlanta,
  projectName,
  onConfirm,
  onRoomsChange,
  fullscreen = false,
}) {
  // ── IMAGE ASPECT RATIO ──
  // Load image to get real dimensions — bboxes are normalized to image space
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });

  useEffect(() => {
    if (!imagemPlanta) return;
    const img = new window.Image();
    img.onload = () => {
      setImgSize({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    };
    img.src = imagemPlanta;
  }, [imagemPlanta]);

  // Virtual dimensions preserving image aspect ratio
  // If image is 1200×800 (1.5:1), virtualW=1000, virtualH=666
  const virtualW = useMemo(() => {
    const aspect = imgSize.w / imgSize.h;
    return aspect >= 1 ? VIRTUAL_SIZE : VIRTUAL_SIZE * aspect;
  }, [imgSize]);

  const virtualH = useMemo(() => {
    const aspect = imgSize.w / imgSize.h;
    return aspect >= 1 ? VIRTUAL_SIZE / aspect : VIRTUAL_SIZE;
  }, [imgSize]);

  // ── DETECT GEOMETRY MODE ──
  // New motor: geometry.vertices (polygons) | Old motor: bbox (rectangles)
  const hasGeometry = useMemo(() =>
    initialComodos?.some(c => c.geometry?.vertices?.length >= 3),
  [initialComodos]);

  // ── GRID SNAP (new path: geometry → snapped polygons + walls) ──
  const rawGeometryRooms = useMemo(() => {
    if (!hasGeometry) return [];
    return initialComodos
      .filter(c => c.geometry?.vertices?.length >= 3)
      .map(c => ({
        nome: c.nome,
        vertices: c.geometry.vertices,
      }));
  }, [hasGeometry, initialComodos]);

  const { snappedRooms: snappedGeo, walls: geoWalls, anchor } = useGridSnap(rawGeometryRooms, virtualW, virtualH);

  // ── TRANSFORM MOTOR DATA → CANVAS DATA ──
  const SHRINK = 0.12; // Shrink factor for bbox fallback path

  const transformedRooms = useMemo(() => {
    if (!initialComodos || initialComodos.length === 0) return [];

    return initialComodos.map((c, idx) => {
      // Determine family for acabamentos
      const familia = c.familia || c.fam || 'social';
      const wet = isWetRoom(familia);

      // Build acabamentos from motor data
      const acabMotor = c.acabamento || c.acabamento_padrao || {};
      const acab = {
        piso: acabMotor.tipoPiso || acabMotor.piso || '',
        parede: wet ? '' : (acabMotor.tipoRevParede || acabMotor.parede || ''),
        rev: wet ? (acabMotor.tipoRevParede || acabMotor.rev || '') : '',
        rodape: wet ? '' : (acabMotor.tipoRodape || acabMotor.rodape || ''),
        teto: acabMotor.forro || acabMotor.teto || '',
      };

      const reqFields = wet ? ['piso', 'rev', 'teto'] : ['piso', 'parede', 'rodape', 'teto'];
      const ok = reqFields.every(k => acab[k] && acab[k] !== '');

      // ── GEOMETRY PATH (new: polygons from Vision + Grid Snap) ──
      const snapped = snappedGeo.find(s => s.nome === c.nome);
      if (snapped && snapped.vertices) {
        // Helper: find closest edge of polygon to a point, return {direction, snapX, snapY}
        // direction: 'h' (horizontal wall) → rot=0 | 'v' (vertical wall) → rot=90
        const findClosestEdge = (px, py, verts) => {
          let bestDist = Infinity;
          let bestEdge = null;
          for (let i = 0; i < verts.length; i++) {
            const [x1, y1] = verts[i];
            const [x2, y2] = verts[(i + 1) % verts.length];
            // Project point onto edge
            const dx = x2 - x1, dy = y2 - y1;
            const len2 = dx * dx + dy * dy;
            if (len2 === 0) continue;
            const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
            const projX = x1 + t * dx;
            const projY = y1 + t * dy;
            const dist = Math.hypot(px - projX, py - projY);
            if (dist < bestDist) {
              bestDist = dist;
              const isVertical = Math.abs(dx) < Math.abs(dy);
              bestEdge = {
                direction: isVertical ? 'v' : 'h',
                snapX: projX,
                snapY: projY,
                t,
                edgeStart: [x1, y1],
                edgeEnd: [x2, y2],
              };
            }
          }
          return bestEdge;
        };

        // Build esquadrias from geometry esquadrias if available
        const geoEsqs = (c.geometry?.esquadrias || []).map((ge, eIdx) => {
          // Apply the SAME anchor remap used for room vertices in useGridSnap
          // This keeps esquadrias aligned to the remapped room coordinate frame
          let nx = ge.position[0];
          let ny = ge.position[1];
          if (anchor) {
            nx = anchor.PAD + ((nx - anchor.minX) / anchor.rangeX) * (1 - 2 * anchor.PAD);
            ny = anchor.PAD + ((ny - anchor.minY) / anchor.rangeY) * (1 - 2 * anchor.PAD);
          }
          // Snap esquadria position to nearest edge of polygon (in virtual coords)
          const vxPoint = nx * virtualW;
          const vyPoint = ny * virtualH;
          const edge = findClosestEdge(vxPoint, vyPoint, snapped.vertices);
          const rot = edge?.direction === 'v' ? 90 : 0;
          const szPx = ge.tipo === 'porta' ? 65 : 70;
          // Center esquadria along edge: subtract half-size in edge direction
          let px = edge?.snapX ?? vxPoint;
          let py = edge?.snapY ?? vyPoint;
          if (edge?.direction === 'h') px -= szPx / 2;
          else py -= szPx / 2;
          return {
            t: ge.tipo,
            c: `${ge.tipo === 'porta' ? 'P' : 'J'}${eIdx + 1}`,
            d: '',
            m: ge.tipo === 'porta' ? 'madeira' : 'aluminio',
            dm: ge.tipo === 'porta' ? '0.80×2.10' : '1.00×1.00',
            px,
            py,
            rot,
            sz: szPx,
            quantidade: 1,
          };
        });

        // Also merge motor esquadrias data (materials, dimensions)
        const motorEsqs = (c.esquadrias || []).map((e, eIdx) => ({
          t: (e.tipo || '').toLowerCase().includes('porta') ? 'porta' : 'janela',
          c: e.codigo || e.tipo || `${e.tipo}-${eIdx}`,
          d: e.descricao || '',
          m: (e.material || 'madeira').toLowerCase(),
          dm: e.dimensoes || (e.tipo === 'Porta' ? '0.80×2.10' : '1.00×1.00'),
          px: snapped.ax + snapped.aw * ((eIdx + 1) / ((c.esquadrias || []).length + 1)),
          py: snapped.ay,
          rot: 0,
          sz: (e.tipo || '').toLowerCase().includes('porta') ? 65 : 70,
          quantidade: e.quantidade || 1,
        }));

        // Prefer geometry esquadrias if available, otherwise motor
        const esqs = geoEsqs.length > 0 ? geoEsqs : motorEsqs;

        return {
          id: idx,
          nome: c.nome || `Cômodo ${idx + 1}`,
          area: c.area || 0,
          fam: familia,
          familia: familia,
          confianca: c.confianca || 'media',
          conf: c.confianca || 'media',
          vertices: snapped.vertices, // POLYGON data
          ax: snapped.ax,
          ay: snapped.ay,
          aw: snapped.aw,
          ah: snapped.ah,
          bbox: c.bbox || { x: 0, y: 0, w: 0.2, h: 0.2 },
          esq: esqs,
          acab,
          ok,
          pavimento: c.pavimento || 'Térreo',
          quantidade: c.quantidade || 1,
        };
      }

      // ── BBOX FALLBACK PATH (old: rectangles) ──
      let bbox = c.bbox || { x: 0, y: 0, w: 0.2, h: 0.2 };
      const shrinkW = bbox.w * SHRINK;
      const shrinkH = bbox.h * SHRINK;
      bbox = {
        x: bbox.x + shrinkW / 2,
        y: bbox.y + shrinkH / 2,
        w: bbox.w - shrinkW,
        h: bbox.h - shrinkH,
      };

      const esqs = (c.esquadrias || []).map((e, eIdx) => ({
        t: (e.tipo || '').toLowerCase().includes('porta') ? 'porta' : 'janela',
        c: e.codigo || e.tipo || `${e.tipo}-${eIdx}`,
        d: e.descricao || '',
        m: (e.material || 'madeira').toLowerCase(),
        dm: e.dimensoes || (e.tipo === 'Porta' ? '0.80×2.10' : '1.00×1.00'),
        px: bbox.x * virtualW + (bbox.w * virtualW * (eIdx + 1)) / ((c.esquadrias || []).length + 1),
        py: bbox.y * virtualH,
        rot: 0,
        sz: (e.tipo || '').toLowerCase().includes('porta') ? 65 : 70,
        quantidade: e.quantidade || 1,
      }));

      return {
        id: idx,
        nome: c.nome || `Cômodo ${idx + 1}`,
        area: c.area || 0,
        fam: familia,
        familia: familia,
        confianca: c.confianca || 'media',
        conf: c.confianca || 'media',
        ax: bbox.x * virtualW,
        ay: bbox.y * virtualH,
        aw: bbox.w * virtualW,
        ah: bbox.h * virtualH,
        bbox: c.bbox || { x: 0, y: 0, w: 0.2, h: 0.2 },
        esq: esqs,
        acab,
        ok,
        pavimento: c.pavimento || 'Térreo',
        quantidade: c.quantidade || 1,
      };
    });
  }, [initialComodos, virtualW, virtualH, snappedGeo, anchor]);

  // ── STATE ──
  const [rooms, setRooms] = useState([]);
  const [intWalls, setIntWalls] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedWall, setSelectedWall] = useState(null);
  const [selectedEsq, setSelectedEsq] = useState(null);
  const [showDimensions, setShowDimensions] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(0.25);

  const canvasContainerRef = useRef(null);
  const stageRef = useRef(null);

  // Update rooms when transform changes
  useEffect(() => {
    if (transformedRooms.length > 0) {
      setRooms(transformedRooms);
    }
  }, [transformedRooms]);

  // External wall envelope = full image virtual space
  // Both geometry and bbox rooms use coords in [0, virtualW] × [0, virtualH]
  // Using full image space ensures the overlay image aligns with room positions
  const extW = virtualW;
  const extH = virtualH;

  // Infer walls — use geoWalls if geometry available, otherwise infer from room adjacency
  const { walls: inferredWalls } = useWallInference(rooms, extW, extH);
  const allWalls = useMemo(() => {
    if (hasGeometry && geoWalls.length > 0) {
      return [...geoWalls, ...intWalls]; // Grid Snap walls + manually added
    }
    return [...inferredWalls, ...intWalls]; // Fallback: inferred + manual
  }, [hasGeometry, geoWalls, inferredWalls, intWalls]);

  // ── TOOLS ──
  const {
    tool, setTool, editMode, isPan,
    specialMode, enterSpecialMode, cancelSpecialMode,
  } = useCanvasTools();

  // ── UNDO/REDO ──
  const getState = useCallback(() => ({
    rooms: rooms,
    intWalls: intWalls,
  }), [rooms, intWalls]);

  const restoreState = useCallback((state) => {
    setRooms(state.rooms);
    setIntWalls(state.intWalls);
  }, []);

  const { saveState, undo, redo, canUndo, canRedo } = useUndoRedo(getState, restoreState);

  // ── ROOM SELECTION ──
  const handleSelectRoom = useCallback((idx) => {
    setSelectedRoom(idx);
    setSelectedWall(null);
    setSelectedEsq(null);
  }, []);

  const handleCloseRightPanel = useCallback(() => {
    setSelectedRoom(null);
  }, []);

  // ── ACABAMENTOS ──
  const handleAcabamentoChange = useCallback((roomIdx, field, value) => {
    saveState();
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[roomIdx] };
      room.acab = { ...room.acab, [field]: value };

      // Recalculate ok status
      const wet = isWetRoom(room.familia || room.fam);
      const reqFields = wet ? ['piso', 'rev', 'teto'] : ['piso', 'parede', 'rodape', 'teto'];
      room.ok = reqFields.every(k => room.acab[k] && room.acab[k] !== '');

      updated[roomIdx] = room;
      return updated;
    });
  }, [saveState]);

  const handleNameChange = useCallback((roomIdx, newName) => {
    saveState();
    setRooms(prev => {
      const updated = [...prev];
      updated[roomIdx] = { ...updated[roomIdx], nome: newName };
      return updated;
    });
  }, [saveState]);

  // ── ESQUADRIAS ──
  const handleAddEsquadria = useCallback((roomIdx, tipo) => {
    saveState();
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[roomIdx] };
      const esq = [...(room.esq || [])];
      esq.push({
        t: tipo,
        c: `${tipo === 'porta' ? 'P' : 'J'}-new`,
        d: 'Adicionada',
        m: tipo === 'porta' ? 'madeira' : 'aluminio',
        dm: tipo === 'porta' ? '0.80×2.10' : '1.00×1.00',
        px: room.ax + room.aw / 2,
        py: room.ay,
        rot: 0,
        sz: tipo === 'porta' ? 65 : 70,
      });
      room.esq = esq;
      updated[roomIdx] = room;
      return updated;
    });
  }, [saveState]);

  const handleRemoveEsquadria = useCallback((roomIdx, esqIdx) => {
    saveState();
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[roomIdx] };
      const esq = [...(room.esq || [])];
      esq.splice(esqIdx, 1);
      room.esq = esq;
      updated[roomIdx] = room;
      return updated;
    });
  }, [saveState]);

  const handleEsquadriaMatChange = useCallback((roomIdx, esqIdx, value) => {
    saveState();
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[roomIdx] };
      const esq = [...(room.esq || [])];
      esq[esqIdx] = { ...esq[esqIdx], m: value };
      room.esq = esq;
      updated[roomIdx] = room;
      return updated;
    });
  }, [saveState]);

  // ── STAGE ACTIONS ──
  const handleStageClick = useCallback(() => {
    setSelectedRoom(null);
    setSelectedWall(null);
    setSelectedEsq(null);
  }, []);

  // ── ZOOM ──
  const getKonvaStage = useCallback(() => {
    return stageRef.current?.getStage?.();
  }, []);

  const handleZoomIn = useCallback(() => {
    const stage = getKonvaStage();
    if (!stage) return;
    const sc = stage.scaleX() * 1.2;
    stage.scale({ x: sc, y: sc });
    stage.draw();
  }, [getKonvaStage]);

  const handleZoomOut = useCallback(() => {
    const stage = getKonvaStage();
    if (!stage) return;
    const sc = stage.scaleX() / 1.2;
    stage.scale({ x: sc, y: sc });
    stage.draw();
  }, [getKonvaStage]);

  const handleFitView = useCallback(() => {
    const stage = getKonvaStage();
    if (!stage) return;
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.draw();
  }, [getKonvaStage]);

  // ── EXPORT PNG ──
  const handleExportPNG = useCallback(() => {
    const stage = getKonvaStage();
    if (!stage) return;
    const uri = stage.toDataURL({ pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = 'qco-plan.png';
    link.href = uri;
    link.click();
  }, [getKonvaStage]);

  // ── CONFIRM (calculate budget) ──
  const handleConfirm = useCallback(() => {
    // Convert rooms back to the format OrcamentoConfirmar expects
    // Use original bbox stored on each room (pre-shrink, normalized 0-1)
    const output = rooms.map(r => ({
      ...r,
      bbox: r.bbox || {
        x: r.ax / virtualW,
        y: r.ay / virtualH,
        w: r.aw / virtualW,
        h: r.ah / virtualH,
      },
      acabamento: {
        tipoPiso: r.acab.piso,
        tipoRevParede: r.acab.parede || r.acab.rev,
        tipoRodape: r.acab.rodape || '',
        forro: r.acab.teto,
      },
      esquadrias: (r.esq || []).map(e => ({
        tipo: e.t === 'porta' ? 'Porta' : 'Janela',
        material: e.m,
        dimensoes: e.dm,
        quantidade: e.quantidade || 1,
      })),
    }));
    onConfirm && onConfirm(output);
  }, [rooms, onConfirm, virtualW, virtualH]);

  // ── KEYBOARD SHORTCUTS ──
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'v' || e.key === 'V') { if (!e.ctrlKey && !e.metaKey) setTool('select'); }
      if (e.key === 'h' || e.key === 'H') setTool('pan');
      if (e.key === 'e' || e.key === 'E') setTool(tool === 'edit' ? 'select' : 'edit');
      if (e.key === 'w' || e.key === 'W') {
        if (!e.ctrlKey) {
          if (specialMode === 'addwall') cancelSpecialMode();
          else enterSpecialMode('addwall');
        }
      }
      if (e.key === 'Escape') {
        cancelSpecialMode();
        setSelectedRoom(null);
        setSelectedWall(null);
        setSelectedEsq(null);
      }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) { e.preventDefault(); undo(); }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) { e.preventDefault(); redo(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedEsq || selectedWall)) {
        // Delete handling would go here
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tool, specialMode, selectedEsq, selectedWall, setTool, cancelSpecialMode, enterSpecialMode, undo, redo]);

  // NOTE: We do NOT auto-sync rooms to parent on every change
  // to avoid infinite re-render loops. Parent gets data via onConfirm.

  // ── RENDER ──
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 500,
      background: '#FFFFFF',
      borderRadius: fullscreen ? 0 : 12,
      overflow: 'hidden',
      border: fullscreen ? 'none' : '1px solid #E5E7EB',
      fontFamily: 'Inter, sans-serif',
    }}>
      {/* Toolbar */}
      <Toolbar
        tool={tool}
        onSetTool={setTool}
        specialMode={specialMode}
        onSpecialMode={enterSpecialMode}
        onCancelSpecial={cancelSpecialMode}
        showDimensions={showDimensions}
        onToggleDimensions={() => setShowDimensions(p => !p)}
        showOverlay={showOverlay}
        onToggleOverlay={() => setShowOverlay(p => !p)}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onExportPNG={handleExportPNG}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitView={handleFitView}
        selectedEsq={selectedEsq}
        selectedWall={selectedWall}
        projectName={projectName}
      />

      {/* Main content: left panel + canvas + right panel */}
      <div style={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* Left panel */}
        <LeftPanel
          rooms={rooms}
          selectedRoom={selectedRoom}
          onSelectRoom={handleSelectRoom}
        />

        {/* Canvas */}
        <div
          ref={canvasContainerRef}
          style={{
            flex: 1,
            background: '#FAFAFA',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CanvasStage
            ref={stageRef}
            rooms={rooms}
            intWalls={allWalls}
            hasGeometry={hasGeometry}
            extW={extW}
            extH={extH}
            selectedRoom={selectedRoom}
            selectedWall={selectedWall}
            selectedEsq={selectedEsq}
            editMode={editMode}
            isPan={isPan}
            showDimensions={showDimensions}
            showOverlay={showOverlay}
            overlayOpacity={overlayOpacity}
            overlayImage={imagemPlanta}
            onSelectRoom={handleSelectRoom}
            onSelectWall={setSelectedWall}
            onSelectEsq={setSelectedEsq}
            onRoomRightClick={() => {}} // Phase 3
            onStageClick={handleStageClick}
            containerRef={canvasContainerRef}
          />

          {/* Overlay opacity slider (when overlay active) */}
          {showOverlay && (
            <div style={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              background: 'rgba(255,255,255,0.95)',
              padding: '6px 12px',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontFamily: 'Inter, sans-serif',
              color: '#6B7280',
            }}>
              <span>Opacidade</span>
              <input
                type="range"
                min="5"
                max="80"
                value={overlayOpacity * 100}
                onChange={(e) => setOverlayOpacity(parseInt(e.target.value) / 100)}
                style={{ width: 80 }}
              />
              <span>{Math.round(overlayOpacity * 100)}%</span>
            </div>
          )}
        </div>

        {/* Right panel */}
        <RightPanel
          room={selectedRoom !== null ? rooms[selectedRoom] : null}
          roomIndex={selectedRoom}
          isOpen={selectedRoom !== null}
          onClose={handleCloseRightPanel}
          onAcabamentoChange={handleAcabamentoChange}
          onNameChange={handleNameChange}
          onAddEsquadria={handleAddEsquadria}
          onRemoveEsquadria={handleRemoveEsquadria}
          onEsquadriaMatChange={handleEsquadriaMatChange}
        />
      </div>

      {/* Bottom bar */}
      <BottomBar
        rooms={rooms}
        onCalcular={handleConfirm}
      />
    </div>
  );
}
